// Radha — the six batch modules' data, straight from PostgREST.
//
// Cashflow, CRM, Invoicing, Reconciliation, Forecasts, Reminders. Each is the
// same pattern as useCollectionsDirect: a typed read scoped by RLS (migrations
// 020–025), no tenant id in any query. Grouped in one file because each is a
// single primary read; they still render as separate tabs.
import { useQuery } from "@tanstack/react-query";

import { supabase } from "../lib/supabase";

function reader<T>(key: string, table: string, columns: string, orderBy: string, asc = false) {
  return () =>
    useQuery({
      queryKey: [key, table],
      queryFn: async (): Promise<T[]> => {
        const { data, error } = await supabase
          .from(table)
          .select(columns)
          .order(orderBy, { ascending: asc })
          .limit(200);
        if (error) throw error;
        return (data ?? []) as unknown as T[];
      },
      staleTime: 30_000,
    });
}

// ---- Cashflow --------------------------------------------------------------
export interface CashflowPeriod {
  id: string; period_month: string; inflows: string; outflows: string;
  net_cashflow: string; opening_balance: string; closing_balance: string; status: string;
}
export const useCashflowPeriods = reader<CashflowPeriod>(
  "cashflow-direct", "cashflow_periods",
  "id, period_month, inflows, outflows, net_cashflow, opening_balance, closing_balance, status",
  "period_month", true,
);

// ---- CRM -------------------------------------------------------------------
export interface CrmDeal {
  id: string; deal_name: string; customer_name: string; stage: string;
  amount: string; win_probability: string; weighted_value: string;
  owner_name: string | null; expected_close: string | null; next_step: string | null;
  last_activity_at: string | null;
  loss_reason: string | null; competitor: string | null;
}
export const useCrmDeals = reader<CrmDeal>(
  "crm-direct", "crm_deals",
  "id, deal_name, customer_name, stage, amount, win_probability, weighted_value, owner_name, expected_close, next_step, last_activity_at, loss_reason, competitor",
  "weighted_value",
);

// ---- Invoicing -------------------------------------------------------------
export interface Invoice {
  id: string; invoice_number: string; customer_name: string; customer_gstin: string | null;
  taxable_amount: string; tax_amount: string; total_amount: string; amount_paid: string;
  irn: string | null; irn_required: boolean; irn_status: string | null;
  place_of_supply: string | null; status: string; issue_date: string; due_date: string;
  zoho_invoice_id: string | null; zoho_customer_id: string | null;
  // FX (070/071). currency is the invoice's OWN unit — line amounts are shown
  // in it; only totals convert. fx_rate_at_issue is the rate fixed at initial
  // recognition, so it is the carrying rate between revaluations.
  currency: string; fx_rate_at_issue: string | null;
  // Country-neutral clearance state (072). irn/irn_status above are the India
  // aliases, kept in sync by a trigger; new code reads these.
  clearance_regime: string | null; clearance_id: string | null;
  clearance_status: string | null; cleared_at: string | null;
}
export const useInvoices = reader<Invoice>(
  "invoicing-direct", "invoices",
  "id, invoice_number, customer_name, customer_gstin, taxable_amount, tax_amount, total_amount, amount_paid, irn, irn_required, irn_status, place_of_supply, status, issue_date, due_date, zoho_invoice_id, zoho_customer_id, currency, fx_rate_at_issue, clearance_regime, clearance_id, clearance_status, cleared_at",
  "issue_date",
);

// ---- Reconciliation --------------------------------------------------------
export interface BankTxn {
  id: string; utr: string | null; vpa: string | null; payer_name: string | null;
  bank: string | null; amount: string; narration: string | null; value_date: string; status: string;
  source: string;
}
export const useBankTxns = reader<BankTxn>(
  "recon-direct", "bank_transactions",
  "id, utr, vpa, payer_name, bank, amount, narration, value_date, status, source",
  "value_date",
);

// ---- Forecasts -------------------------------------------------------------
export interface RevenueForecast {
  id: string; period_month: string; base_amount: string; bull_amount: string;
  bear_amount: string; actual_amount: string | null; variance_to_base: string | null; status: string;
}
export const useRevenueForecasts = reader<RevenueForecast>(
  "forecasts-direct", "revenue_forecasts",
  "id, period_month, base_amount, bull_amount, bear_amount, actual_amount, variance_to_base, status",
  "period_month", true,
);

// ---- Reminders -------------------------------------------------------------
export interface ReminderSchedule {
  id: string; customer_name: string; account_ref: string | null; channel: string;
  language: string; cadence_days: number; next_run_date: string | null; is_active: boolean;
}
export const useReminderSchedules = reader<ReminderSchedule>(
  "reminders-direct", "reminder_schedules",
  "id, customer_name, account_ref, channel, language, cadence_days, next_run_date, is_active",
  "next_run_date", true,
);
